import {computeMeanGrade, computeMedianGrade} from './dpeGradeUtils';

/**
 * Calcule les colonnes meta synthétiques pour une ligne CSV.
 *
 * @param {Object} row - Ligne CSV { colKey → string | string[] }
 * @returns {{
 *   libelle_adresse:      string,
 *   nb_etages_max:        string,
 *   nb_etages_estimes:    string,
 *   surface_utile_calc:   string,
 *   etiquette_dpe_median: string,
 *   etiquette_dpe_moyen:  string,
 *   etiquette_ges_median: string,
 *   etiquette_ges_moyen:  string,
 * }}
 */
export function computeMetaColumns(row) {

    // ── Helpers partagés ────────────────────────────────────────────────────────

    /** Extrait les valeurs numériques positives d'un champ string|string[] */
    function toNums(val) {
        if (val == null || val === '') return [];
        const arr = Array.isArray(val) ? val : [val];
        return arr.map(Number).filter(n => !isNaN(n) && n > 0);
    }

    /** Normalise un champ en tableau (vide → []) */
    function toArr(val) {
        if (val == null || val === '') return [];
        return Array.isArray(val) ? val : [val];
    }

    // ── Libellé adresse ─────────────────────────────────────────────────────────

    /** Libellé adresse principal de la ligne */
    function libelleAdresse() {
        const libelles = row['adresses.libelle_adresse'];
        const principale = row['adresses.adresse_principale'];
        if (libelles == null || libelles === '') return '';

        // Une seule adresse : on la retourne directement
        if (typeof libelles === 'string') return libelles;

        // Plusieurs adresses : l'index de "t" dans adresse_principale
        // correspond à l'index de la libelle à retourner.
        if (Array.isArray(principale)) {
            const idx = principale.indexOf('t');
            if (idx >= 0 && idx < libelles.length) return libelles[idx];
        }
        // Fallback : première adresse de la liste
        return libelles[0] ?? '';
    }

    // ── Étages ──────────────────────────────────────────────────────────────────

    /** Nombre d'étages max calculé depuis batiment_groupe_synthese_propriete_usage.nb_niveau */
    function nbEtagesMax() {
        const val = row['batiment_groupe_synthese_propriete_usage.nb_niveau'];

        if (val == null || val === '') return '';

        // Valeur unique
        if (typeof val === 'string') return val;

        // Plusieurs valeurs : retourner le maximum numérique
        if (Array.isArray(val)) {
            const nums = val.map(Number).filter(n => !isNaN(n));
            if (!nums.length) return '';
            return String(Math.max(...nums));
        }

        return '';
    }

    /**
     * Retourne la hauteur sous-plafond estimée selon l'année de construction.
     * @param {number|null} annee
     * @returns {number}
     */
    function getDiviseur(annee) {
        if(!annee){
            annee=0;
        }
        if (annee < 1920) return 3.3;
        if (annee < 1948) return 3;
        if (annee < 1978) return 2.8;
        if (annee < 1983) return 2.6;
        return 2.5;
    }





    // ── DPE / GES ────────────────────────────────────────────────────────────────

    /** Étiquette DPE médiane (valeur centrale triée) — 'N' si aucun DPE disponible */
    function etiquetteDpeMedian() {
        return computeMedianGrade(row['dpes.etiquette_dpe']) ?? 'N';
    }

    /** Étiquette DPE moyenne (moyenne arithmétique) — 'N' si aucun DPE disponible */
    function etiquetteDpeMoyen() {
        return computeMeanGrade(row['dpes.etiquette_dpe']) ?? 'N';
    }

    /** Étiquette GES médiane (valeur centrale triée) — 'N' si aucun GES disponible */
    function etiquetteGesMedian() {
        return computeMedianGrade(row['dpes.etiquette_ges']) ?? 'N';
    }

    /** Étiquette GES moyenne (moyenne arithmétique) — 'N' si aucun GES disponible */
    function etiquetteGesMoyen() {
        return computeMeanGrade(row['dpes.etiquette_ges']) ?? 'N';
    }

    function getAnneeMin() {
        const anneeNums = toNums(row['batiment_groupe_synthese_propriete_usage.annee_construction']);
        return anneeNums.length ? Math.min(...anneeNums) : null;
    }

    function getNbEtagesConstruction(annee) {
        const hauteur = toNums(row['batiment_construction.hauteur']);
        let h = hauteur.length ? Math.min(...hauteur) : null;

        return Math.ceil(h / getDiviseur(annee));
    }

    function getNbEtageGroupeReel() {
        const hauteur = [
            ...toNums(row['batiment_groupe_synthese_propriete_usage.nb_niveau'])
        ];
        let h = hauteur.length ? Math.min(...hauteur) : null;
        return h
    }

    function getNbEtageGroupeMax(annee) {
        const hauteur = [
            ...toNums(row['batiment_groupe_bdtopo_bat.hauteur_mean']),
            ...toNums(row['batiment_groupe_bdtopo_bat.max_hauteur']),
        ];
        let h = hauteur.length ? Math.min(...hauteur) : null;
        return Math.ceil(h / getDiviseur(annee));
    }

    function getSurfaceUtileConstruction() {
        const surfaces = toNums(row['batiment_construction.s_geom_cstr']);
        let total = 0
        surfaces.forEach(s => {
            total += s
        });

        return total;
    }

    function getSurfaceGroupe() {
        const surfaces = toNums(row['batiment_groupe.s_geom_groupe']);
        let total = 0
        surfaces.forEach(s => {
            total += s
        });

        return total;
    }

    /** Extrait la première valeur d'un champ (tableau ou scalaire) */
    function first(val) {
        return Array.isArray(val) ? val[0] : val;
    }

    if (first(row["meta.index"]) === first(row["meta.cle_interop_adr"])) {
        let libelle_adresse = libelleAdresse();
        let annee = getAnneeMin();
        let nbEtages = getNbEtagesConstruction(annee);
        let surface = getSurfaceUtileConstruction(nbEtages)
        return {
            libelle_adresse: libelle_adresse,
            nb_etages_estimes_adresse: nbEtages,
            surface_utile_calc_adresse: surface * nbEtages * 0.9,
            etiquette_dpe_median: etiquetteDpeMedian(),
            etiquette_dpe_moyen: etiquetteDpeMoyen(),
            etiquette_ges_median: etiquetteGesMedian(),
            etiquette_ges_moyen: etiquetteGesMoyen(),
        };
    } else if (first(row["meta.index"]) === first(row["meta.batiment_construction_id"])) {
        let libelle_adresse = libelleAdresse();
        let annee = getAnneeMin();
        let nbEtages = getNbEtagesConstruction(annee);
        let surface = getSurfaceUtileConstruction(nbEtages)

        return {
            libelle_adresse: libelle_adresse,
            nb_etages_estimes_construction: nbEtages,
            surface_utile_calc_construction: surface * nbEtages * 0.9,
            etiquette_dpe_median: etiquetteDpeMedian(),
            etiquette_dpe_moyen: etiquetteDpeMoyen(),
            etiquette_ges_median: etiquetteGesMedian(),
            etiquette_ges_moyen: etiquetteGesMoyen(),
        };
    } else if (first(row["meta.index"]) === first(row["meta.batiment_groupe_id"])) {
        let libelle_adresse = libelleAdresse();
        let annee = getAnneeMin();
        let nbEtagesReelGroupe = getNbEtageGroupeReel();
        let nbEtagesEstimeGroupe = getNbEtageGroupeMax(annee);
        let surfaceGroupe = getSurfaceGroupe();
        return {
            libelle_adresse: libelle_adresse,
            nb_etages_reel_groupe: nbEtagesReelGroupe,
            nb_etages_estimes_groupe: nbEtagesEstimeGroupe,
            surface_utile_reel_groupe: surfaceGroupe * nbEtagesReelGroupe * 0.9,
            surface_utile_calc_groupe: surfaceGroupe * nbEtagesEstimeGroupe * 0.9,
            etiquette_dpe_median: etiquetteDpeMedian(),
            etiquette_dpe_moyen: etiquetteDpeMoyen(),
            etiquette_ges_median: etiquetteGesMedian(),
            etiquette_ges_moyen: etiquetteGesMoyen(),
        };
    } else if (first(row["meta.index"]) === first(row["meta.parcelle_id"])) {
        let libelle_adresse = libelleAdresse();
        let annee = getAnneeMin();
        let nbEtagesReelGroupe = getNbEtageGroupeReel();
        let nbEtagesEstimeGroupe = getNbEtageGroupeMax(annee);
        let surfaceGroupe = getSurfaceGroupe();
        return {
            libelle_adresse: libelle_adresse,
            nb_etages_reel_parcelle: nbEtagesReelGroupe,
            nb_etages_estimes_parcelle: nbEtagesEstimeGroupe,
            surface_utile_reel_parcelle: surfaceGroupe * nbEtagesReelGroupe * 0.9,
            surface_utile_calc_parcelle: surfaceGroupe * nbEtagesEstimeGroupe * 0.9,

            etiquette_dpe_median: etiquetteDpeMedian(),
            etiquette_dpe_moyen: etiquetteDpeMoyen(),
            etiquette_ges_median: etiquetteGesMedian(),
            etiquette_ges_moyen: etiquetteGesMoyen(),
        };

    }

}

